import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { PageHero, Section, Container, Btn, ArrowLink, SectionHead, Accordion, Stat } from "@/components/kit";
import { Reveal, Eyebrow } from "@/lib/anim";
import { SERVICES, CASES } from "@/lib/content";
import { SERVICE_IMG } from "@/lib/images";
import { AMT } from "@/components/Offer";
import PayDialog from "@/components/PayDialog";
import NotFound from "@/pages/NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const svc = SERVICES[slug];
  const [tier, setTier] = useState(null);
  if (!svc) return <NotFound />;

  const primaryCta = (p, variant = "clay", key) => {
    if (p.tier && AMT[p.tier]) {
      return <Btn key={key} variant={variant} onClick={() => setTier({ id: p.tier, label: svc.title, amount: AMT[p.tier] })} data-testid="service-primary-cta">{p.label}</Btn>;
    }
    return <Btn key={key} to="/book" variant={variant} data-testid="service-primary-cta">{p.label || "Book a fit call"}</Btn>;
  };

  const crumbs = [{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: svc.title.split(",")[0] }];
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Service", name: svc.title,
    serviceType: svc.seoTitle || svc.title,
    provider: { "@type": "ProfessionalService", name: "Prox", url: "https://prox-au.com" },
    areaServed: [{ "@type": "Country", name: "Australia" }, { "@type": "Country", name: "New Zealand" }],
    description: svc.seoDesc || svc.lead,
  };

  return (
    <>
      <Seo title={svc.seoTitle || svc.title} canonical={`/services/${slug}`} description={svc.seoDesc || svc.lead} keywords={svc.seoKeywords} jsonLd={jsonLd} crumbs={crumbs} />
      <PageHero
        eyebrow={svc.eyebrow}
        crumbs={crumbs}
        title={svc.title}
        lead={svc.lead}
        actions={[primaryCta(svc.primary, "clay", "cta"), <Btn key="p" to="/pricing" variant="outline">See pricing</Btn>]}
      />

      <Section bg="bone" pad={false} className="py-14 md:py-16">
        <div className="aspect-[21/9] overflow-hidden" style={{ borderRadius: 2 }}>
          <img src={SERVICE_IMG[slug]} alt="" className="img-cover duo" loading="lazy" />
        </div>
      </Section>

      {/* For you / not for you */}
      <Section bg="paper">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <Eyebrow>This is for you if</Eyebrow>
            <div className="prose max-w-none"><ul>{svc.forYou.map((f) => <li key={f}>{f}</li>)}</ul></div>
          </div>
          <div>
            <Eyebrow>This is not for you if</Eyebrow>
            <div className="prose max-w-none"><ul>{svc.notForYou.map((f) => <li key={f}>{f}</li>)}</ul></div>
          </div>
        </div>
      </Section>

      {/* Problem */}
      <Section bg="sand">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><SectionHead eyebrow="The problem" title="Where the money leaks." /></div>
          <div className="lg:col-span-8 prose max-w-none"><p>{svc.problem}</p></div>
        </div>
      </Section>

      {/* Process */}
      <Section bg="bone">
        <SectionHead eyebrow="How it works" title="The steps, in order." />
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-8 mt-12">
          {svc.process.map((p, i) => (
            <Reveal i={i % 2} key={p}>
              <div className="flex gap-5">
                <div className="font-mono text-[var(--clay)] text-sm pt-1 w-8 shrink-0">0{i + 1}</div>
                <p className="t-body">{p}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Receive + proof */}
      <Section bg="charcoal" dark>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Eyebrow>What you receive</Eyebrow>
            <ul className="mt-5 space-y-3">
              {svc.receive.map((r) => (
                <li key={r} className="flex gap-3 t-body"><span className="mt-[10px] w-1.5 h-1.5 bg-[var(--clay)] shrink-0" aria-hidden /><span className="text-[var(--stone)]">{r}</span></li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-white/12 p-8" style={{ borderRadius: 2 }}>
              <Stat n={svc.proof.stat} c={svc.proof.cap} dark />
              {svc.proof.link && CASES[svc.proof.link] && (
                <ArrowLink to={`/case-studies/${svc.proof.link}`} dark className="mt-8">{svc.proof.linkText}</ArrowLink>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* Price */}
      <Section bg="paper">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <Eyebrow>Price</Eyebrow>
            <p className="t-lead measure">{svc.price.text}</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">{primaryCta(svc.primary)}</div>
        </div>
      </Section>

      {/* FAQ */}
      <Section bg="bone">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><SectionHead eyebrow="Questions" title="Answered plainly." /></div>
          <div className="lg:col-span-8"><Accordion items={svc.faq} testid="service-faq" /></div>
        </div>
      </Section>

      {/* Related */}
      <Section bg="sand">
        <Eyebrow>Related work</Eyebrow>
        <div className="grid md:grid-cols-2 gap-5 mt-4">
          {svc.related.map(([rslug, rtitle]) => (
            <Link key={rslug} to={`/services/${rslug}`} className="card p-7 flex items-center justify-between" data-testid={`related-${rslug}`}>
              <span className="t-h4">{rtitle}</span>
              <span className="arrow-anim text-[var(--clay-deep)]">→</span>
            </Link>
          ))}
        </div>
      </Section>

      {/* Closing */}
      <Section bg="charcoalDeep" dark className="py-20 md:py-24">
        <div className="max-w-3xl">
          <h2 className="t-display2">{svc.closing.h}</h2>
          <div className="mt-9 flex flex-wrap gap-4">
            {primaryCta(svc.closing)}
            <Btn to="/book" variant="outline-bone">Book a fit call first</Btn>
          </div>
        </div>
      </Section>

      {tier && <PayDialog tier={tier} onClose={() => setTier(null)} />}
    </>
  );
}
